import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ActionSuccessModal } from "@/components/gamification/ActionSuccessModal";
import {
  Camera,
  X,
  Check,
  RotateCcw,
  ArrowLeft,
  Home,
  Bird,
  Skull,
  UtensilsCrossed,
  Droplets,
  Stethoscope,
  Leaf,
  Sprout,
  Lightbulb,
  CheckCircle,
  Loader2,
  LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUploadPhoto } from "@/hooks/api/usePhotos";
import { pollForFinding, type PollHandle } from "@/features/photo/analysisPoll";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { useCBSBatchStore } from "@/stores/cbsBatchStore";
import { supabase } from "@/integrations/supabase/client";
import { gatherPreCaptureMetadata } from "@/utils/photoPreCapture";
import type { Database } from "@/integrations/supabase/types";

// Simple Mode context with fallback
let useSimpleMode: () => { isSimpleMode: boolean };
try {
  const context = require("@/contexts/SimpleModeContext");
  useSimpleMode = context.useSimpleMode;
} catch {
  useSimpleMode = () => ({ isSimpleMode: false });
}

type PhotoCategory = Database["public"]["Enums"]["photo_category"];
type CameraMode = "preview" | "captured";

// Map UI category IDs to DB enum values
type UICategoryId = "shed_exterior" | "shed_interior" | "bird_health" | "feed_storage" | "mortality_evidence" | "water_system";

const categoryToDbEnum: Record<UICategoryId, PhotoCategory> = {
  shed_exterior: "shed",
  shed_interior: "shed",
  bird_health: "birds",
  feed_storage: "feed_delivery",
  mortality_evidence: "mortality",
  water_system: "equipment",
};

interface Category {
  id: UICategoryId;
  emoji: string;
  labelKey: string;
  labelFallback: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

interface AnalysisResult {
  status: 'excellent' | 'good' | 'fair' | 'stressed' | 'critical';
  main_finding: string;
  action: 'none' | 'see_agriagent' | 'see_community' | 'contact_supervisor';
  next_check: 'today' | '3_days' | '1_week' | 'next_cycle';
}

const GENERIC_ANALYSIS: AnalysisResult = { status: 'good', main_finding: '', action: 'none', next_check: 'next_cycle' };

// FIX-CBS-B2-SERVER-CHAIN: the farmer's own session read path (authenticated client, x-session-token,
// RLS "Session based read photos"). Reads the photo's ai_analysis; null until the server-side analysis
// has landed. `.limit(1)`, never `.single()`, so an empty read is 200 [] and never a 406.
const readPhotoAnalysis = async (photoId: string): Promise<unknown> => {
  const { data, error } = await getAuthenticatedClient().from('photos').select('ai_analysis').eq('id', photoId).limit(1);
  if (error) throw error;
  return data?.[0]?.ai_analysis ?? null;
};

const categories: Category[] = [
  { id: "shed_exterior", emoji: "🏠", labelKey: "camera.categories.shedExterior", labelFallback: "Shed Exterior", icon: Home, color: "text-info", bgColor: "bg-info" },
  { id: "shed_interior", emoji: "🐔", labelKey: "camera.categories.shedInterior", labelFallback: "Shed Interior", icon: Bird, color: "text-success", bgColor: "bg-success" },
  { id: "bird_health", emoji: "🩺", labelKey: "camera.categories.health", labelFallback: "Health", icon: Stethoscope, color: "text-primary", bgColor: "bg-primary" },
  { id: "feed_storage", emoji: "🌾", labelKey: "camera.categories.feed", labelFallback: "Feed", icon: UtensilsCrossed, color: "text-warning", bgColor: "bg-warning" },
  { id: "mortality_evidence", emoji: "☠️", labelKey: "camera.categories.mortality", labelFallback: "Mortality", icon: Skull, color: "text-destructive", bgColor: "bg-destructive" },
  { id: "water_system", emoji: "💧", labelKey: "camera.categories.water", labelFallback: "Water", icon: Droplets, color: "text-info", bgColor: "bg-info" },
];

export const CameraScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isSimpleMode } = useSimpleMode();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = useUploadPhoto();
  const activeBatch = useCBSBatchStore((s) => s.activeBatch);

  const [mode, setMode] = useState<CameraMode>("preview");
  const [selectedCategory, setSelectedCategory] = useState<UICategoryId>("shed_interior");
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisGeneric, setAnalysisGeneric] = useState(false);
  // The finding poll in flight (at most one); cancelled when the modal or the screen closes.
  const pollRef = useRef<PollHandle | null>(null);
  useEffect(() => () => { pollRef.current?.cancel(); pollRef.current = null; }, []);

  // Pre-capture session tracking (for fraud detection / AI context)
  const [sessionId] = useState(() => crypto.randomUUID());
  const [sessionStartedAt] = useState(() => new Date().toISOString());
  const [cameraOpenedAt] = useState(() => Date.now());
  const [sessionPhotoCount, setSessionPhotoCount] = useState(0);

  // Auto-select category based on navigation context
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const contextCategory = params.get('category') as UICategoryId;
    if (contextCategory && categories.find(c => c.id === contextCategory)) {
      setSelectedCategory(contextCategory);
    }
  }, [location.search]);

  const handleCapture = () => {
    if ('vibrate' in navigator) navigator.vibrate(50);
    // Open native camera / file picker
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Flash effect
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 150);

    setCapturedFile(file);
    // Create local preview URL
    const previewUrl = URL.createObjectURL(file);
    setCapturedPreview(previewUrl);
    setMode("captured");

    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRetake = () => {
    if ('vibrate' in navigator) navigator.vibrate(15);
    if (capturedPreview) URL.revokeObjectURL(capturedPreview);
    setCapturedFile(null);
    setCapturedPreview(null);
    setHasVoiceNote(false);
    setMode("preview");
  };

  const handleSave = async () => {
    if ('vibrate' in navigator) navigator.vibrate(15);
    if (!capturedFile) return;

    const params = new URLSearchParams(location.search);
    const batchId = params.get('batchId') || activeBatch?.id || undefined;
    const taskId = params.get('taskId') || undefined;
    const userId = getCurrentUserId();

    // Gather pre-capture metadata BEFORE upload (device, session, geo, context)
    let precapture: Awaited<ReturnType<typeof gatherPreCaptureMetadata>> | undefined;
    if (userId) {
      try {
        precapture = await gatherPreCaptureMetadata({
          userId,
          cycleId: undefined,
          batchId,
          taskId,
          photoCategory: categoryToDbEnum[selectedCategory],
          supabaseClient: supabase,
          sessionId,
          sessionStartedAt,
          cameraOpenedAt,
          sessionPhotoCount,
          isFrontCamera: false,
          flashMode: undefined,
          zoomLevel: undefined,
        });
      } catch {
        precapture = undefined;
      }
    }

    uploadPhoto.mutate(
      {
        file: capturedFile,
        photoCategory: categoryToDbEnum[selectedCategory],
        activityId: undefined,
        batchId,
        precaptureMetadata: precapture as unknown as Record<string, unknown> | undefined,
      },
      {
        onSuccess: (uploadedPhoto) => {
          if (capturedPreview) URL.revokeObjectURL(capturedPreview);
          setSessionPhotoCount((prev) => prev + 1);

          // Show analysis modal immediately (loading state)
          setShowAnalysisModal(true);

          // FIX-CBS-B2-SERVER-CHAIN: the server started the analysis inside upload-photo. As in the CCS
          // app (B1 ruling), wait up to 20 s for the finding, reading the photo row every 2 s through the
          // farmer's session read path; show the finding when it lands, else the generic message.
          pollRef.current?.cancel();
          setAnalysisResult(null);
          setAnalysisGeneric(false);
          if (userId && uploadedPhoto.analysis === 'started') {
            const handle = pollForFinding(uploadedPhoto.id, readPhotoAnalysis);
            pollRef.current = handle;
            handle.promise.then((outcome) => {
              if (pollRef.current !== handle) return;
              pollRef.current = null;
              if (outcome.finding) {
                setAnalysisResult(outcome.finding);
                setAnalysisGeneric(false);
              } else {
                setAnalysisResult(GENERIC_ANALYSIS);
                setAnalysisGeneric(true);
              }
            });
          } else {
            setAnalysisResult(GENERIC_ANALYSIS);
            setAnalysisGeneric(true);
          }
        },
      }
    );
  };

  const handleAnalysisClose = () => {
    pollRef.current?.cancel();
    pollRef.current = null;
    setShowAnalysisModal(false);
    setAnalysisResult(null);
    setShowSuccessModal(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate(-1);
  };

  const handleBack = () => {
    if ('vibrate' in navigator) navigator.vibrate(15);
    if (capturedPreview) URL.revokeObjectURL(capturedPreview);
    navigate(-1);
  };

  const isSaving = uploadPhoto.isPending;

  return (
    <>
      {/* Hidden file input for camera capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelected}
      />

      <div className="fixed inset-0 bg-black overflow-hidden">
        {/* Camera Shutter Flash */}
        <AnimatePresence>
          {showFlash && (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 bg-white z-50 pointer-events-none"
            />
          )}
        </AnimatePresence>

        {/* Full-screen Camera Viewfinder */}
        <div className="absolute inset-0">
          {mode === "preview" ? (
            <div className="w-full h-full bg-gradient-to-br from-neutral-900 to-neutral-800 flex items-center justify-center">
              <Camera className="w-32 h-32 text-white/20" />
            </div>
          ) : (
            <motion.img
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              src={capturedPreview || ""}
              alt="Captured"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Floating Back Button - Top Left */}
        <button
          onClick={handleBack}
          className={cn(
            "absolute top-4 left-4 z-20",
            "w-12 h-12 rounded-full",
            "bg-black/40 backdrop-blur-sm",
            "flex items-center justify-center",
            "active:scale-95 transition-transform",
            "mt-[env(safe-area-inset-top)]"
          )}
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>

        {/* Category Pills - Top (Preview Mode) */}
        {mode === "preview" && (
          <div
            className={cn(
              "absolute top-4 left-16 right-4 z-10",
              "mt-[env(safe-area-inset-top)]"
            )}
          >
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isSelected = selectedCategory === cat.id;

                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      if ('vibrate' in navigator) navigator.vibrate(10);
                      setSelectedCategory(cat.id);
                    }}
                    className={cn(
                      "flex-shrink-0 h-14 rounded-full",
                      "flex items-center justify-center",
                      "transition-all duration-200",
                      "active:scale-95",
                      isSelected
                        ? cn(cat.bgColor, "text-white px-5 scale-105 shadow-lg")
                        : "bg-black/40 backdrop-blur-sm text-white/80 px-4 hover:bg-black/60"
                    )}
                  >
                    <Icon className={cn(
                      isSimpleMode ? "w-8 h-8" : "w-5 h-5"
                    )} />
                    <span className={cn("ml-1 font-medium", isSimpleMode ? "text-base" : "text-xs")}>
                      {t(cat.labelKey, { defaultValue: cat.labelFallback })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Capture Button - Center Bottom (Preview Mode) */}
        {mode === "preview" && (
          <div
            className={cn(
              "absolute bottom-8 left-0 right-0 z-10",
              "flex justify-center",
              "pb-[env(safe-area-inset-bottom)]"
            )}
          >
            <motion.button
              onClick={handleCapture}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              {/* Outer breathing ring */}
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 rounded-full border-4 border-white/30"
                style={{ width: 88, height: 88, margin: -4 }}
              />

              {/* Main capture button */}
              <div
                className={cn(
                  "w-20 h-20 rounded-full",
                  "bg-white shadow-xl",
                  "flex items-center justify-center",
                  "border-4 border-white/50"
                )}
              >
                <div className="w-16 h-16 rounded-full bg-white border-2 border-neutral-200" />
              </div>
            </motion.button>
          </div>
        )}

        {/* Post-Capture Confirmation */}
        {mode === "captured" && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={cn(
              "absolute bottom-0 left-0 right-0 z-10",
              "p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]",
              "bg-gradient-to-t from-black/80 via-black/50 to-transparent"
            )}
          >
            {/* Category Badge */}
            <div className="flex justify-center mb-6">
              {(() => {
                const cat = categories.find(c => c.id === selectedCategory);
                const Icon = cat?.icon || Bird;
                return (
                  <div className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-full",
                    cat?.bgColor,
                    "text-white"
                  )}>
                    <Icon className="w-5 h-5" />
                    {!isSimpleMode && cat && (
                      <span className="text-sm font-medium">
                        {t(cat.labelKey, { defaultValue: cat.labelFallback })}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-6">
              {/* Retake Button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleRetake}
                className={cn(
                  "w-14 h-14 rounded-full",
                  "bg-white/20 backdrop-blur-sm",
                  "border-2 border-white/50",
                  "flex items-center justify-center",
                  "active:bg-white/30"
                )}
              >
                <RotateCcw className="w-7 h-7 text-white" />
              </motion.button>

              {/* Confirm/Save Button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                disabled={isSaving}
                className={cn(
                  "w-14 h-14 rounded-full",
                  "bg-success",
                  "border-2 border-success",
                  "flex items-center justify-center",
                  "shadow-lg shadow-success/30",
                  isSaving && "opacity-70"
                )}
              >
                {isSaving ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-7 h-7 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  <Check className="w-7 h-7 text-white" />
                )}
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>

      {/* AI Analysis Modal */}
      {showAnalysisModal && (
        <Dialog open={showAnalysisModal} onOpenChange={(open) => { if (!open) handleAnalysisClose(); }}>
          <DialogContent className="max-w-sm mx-auto p-0 bg-background overflow-hidden">
            {analysisResult ? (
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    {analysisGeneric ? <CheckCircle className="w-7 h-7 text-primary" /> : <Leaf className="w-7 h-7 text-primary" />}
                  </div>
                  <h3 className="text-lg font-semibold">
                    {analysisGeneric ? t('camera.photoAnalyzed', { defaultValue: 'Photo Saved' }) : t('camera.cropAnalysis', { defaultValue: 'Farm Analysis' })}
                  </h3>
                </div>

                {!analysisGeneric && (
                  <>
                    <div className="flex justify-center">
                      <span className={
                        analysisResult.status === 'excellent' || analysisResult.status === 'good'
                          ? 'px-3 py-1 rounded-full text-sm font-medium bg-success/20 text-success'
                          : analysisResult.status === 'fair'
                          ? 'px-3 py-1 rounded-full text-sm font-medium bg-warning/20 text-warning'
                          : 'px-3 py-1 rounded-full text-sm font-medium bg-destructive/20 text-destructive'
                      }>
                        {analysisResult.status.charAt(0).toUpperCase() + analysisResult.status.slice(1)}
                      </span>
                    </div>

                    {analysisResult.main_finding && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted">
                        <Sprout className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                        <span className="text-sm">{analysisResult.main_finding}</span>
                      </div>
                    )}

                    {analysisResult.action !== 'none' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <Lightbulb className="w-5 h-5 text-primary shrink-0" />
                        <span className="text-sm">
                          {analysisResult.action === 'see_agriagent' && t('camera.actionAgriAgent', { defaultValue: 'Ask your AgriAgent for advice' })}
                          {analysisResult.action === 'see_community' && t('camera.actionCommunity', { defaultValue: 'Check the community network' })}
                          {analysisResult.action === 'contact_supervisor' && t('camera.actionSupervisor', { defaultValue: 'Contact your supervisor' })}
                        </span>
                      </div>
                    )}

                    {analysisResult.next_check !== 'next_cycle' && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4" />
                        <span>
                          {analysisResult.next_check === 'today' && t('camera.nextCheckToday', { defaultValue: 'Check again today' })}
                          {analysisResult.next_check === '3_days' && t('camera.nextCheck3Days', { defaultValue: 'Check again in 3 days' })}
                          {analysisResult.next_check === '1_week' && t('camera.nextCheck1Week', { defaultValue: 'Check again in 1 week' })}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {analysisGeneric && (
                  <p className="text-sm text-muted-foreground text-center">
                    {t('camera.photoSavedAnalyzed', { defaultValue: 'Your photo has been saved and analyzed.' })}
                  </p>
                )}

                <Button className="w-full" onClick={handleAnalysisClose}>
                  {t('common.done', { defaultValue: 'Done' })}
                </Button>
              </div>
            ) : (
              <div className="p-6 space-y-4 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
                <h3 className="text-lg font-semibold">{t('camera.analyzingPhoto', { defaultValue: 'Analyzing your photo...' })}</h3>
                <p className="text-sm text-muted-foreground">{t('camera.analyzingPhotoSubtitle', { defaultValue: 'Your Farm Advisor is reviewing your photo' })}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Success Modal */}
      <ActionSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessClose}
        title={t('camera.success', { defaultValue: 'Photo Saved!' })}
        points={15}
        message={t('camera.successMessage', { defaultValue: 'AI will analyze your photo automatically' })}
      />
    </>
  );
};
